export class AppError extends Error {
  readonly code: string;
  readonly statusCode: number;
  /**
   * Whether the failure underlying this error has ALREADY been reported to
   * error tracking by the layer that threw it. A self-reporting service (e.g.
   * an outbound integration client that captures the upstream boundary error
   * before throwing) sets this `true` so the shared route error sink
   * (`handleApiError`) skips a second `captureException` — one failure, one
   * Sentry event (single-owner posture). Defaults `false`; additive,
   * so no existing throw site changes behaviour.
   */
  readonly reported: boolean;

  constructor(
    message: string,
    options?: {
      code?: string;
      statusCode?: number;
      cause?: unknown;
      reported?: boolean | undefined;
    },
  ) {
    super(message, { cause: options?.cause });
    this.name = 'AppError';
    this.code = options?.code ?? 'APP_ERROR';
    this.statusCode = options?.statusCode ?? 500;
    this.reported = options?.reported ?? false;
  }
}

export class ValidationError extends AppError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, {
      code: 'VALIDATION_ERROR',
      statusCode: 400,
      cause: options?.cause,
    });
    this.name = 'ValidationError';
  }
}

export class NotFoundError extends AppError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, {
      code: 'NOT_FOUND',
      statusCode: 404,
      cause: options?.cause,
    });
    this.name = 'NotFoundError';
  }
}

export class AuthenticationError extends AppError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, {
      code: 'AUTHENTICATION_ERROR',
      statusCode: 401,
      cause: options?.cause,
    });
    this.name = 'AuthenticationError';
  }
}

export class AuthorizationError extends AppError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, {
      code: 'AUTHORIZATION_ERROR',
      statusCode: 403,
      cause: options?.cause,
    });
    this.name = 'AuthorizationError';
  }
}

/**
 * Thrown when a request conflicts with the current state of the resource — e.g.
 * performing an action on a record that has already been completed (a
 * `completedAt`-style guard). Surface as 409 so the client can distinguish
 * "already done" from a generic validation failure.
 */
export class ConflictError extends AppError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, {
      code: 'CONFLICT',
      statusCode: 409,
      cause: options?.cause,
    });
    this.name = 'ConflictError';
  }
}

/** Stable machine code for the retryable ingest-in-progress conflict. */
export const INGEST_IN_PROGRESS_CODE = 'INGEST_IN_PROGRESS';

/**
 * Thrown when an upload file's server-side ingest is still in flight and the
 * requested operation (analyze / review / finalize) needs the fully-ingested
 * rows. A RETRYABLE 409 with its own stable `code` so clients distinguish
 * "poll and retry shortly" from a genuine state conflict (bare `CONFLICT`).
 * `retryAfterMs` is a server-suggested backoff hint carried on the class (and by
 * `apiClient` onto the client-side `AppError`); the wire envelope stays the
 * canonical flat `{ error, code }`.
 */
export class IngestInProgressError extends AppError {
  /** Suggested client wait before retrying, in milliseconds. */
  readonly retryAfterMs: number;

  constructor(message: string, options?: { cause?: unknown; retryAfterMs?: number }) {
    super(message, {
      code: INGEST_IN_PROGRESS_CODE,
      statusCode: 409,
      cause: options?.cause,
    });
    this.name = 'IngestInProgressError';
    this.retryAfterMs = options?.retryAfterMs ?? 1000;
  }
}

export class ExternalServiceError extends AppError {
  constructor(message: string, options?: { cause?: unknown; reported?: boolean }) {
    super(message, {
      code: 'EXTERNAL_SERVICE_ERROR',
      statusCode: 502,
      cause: options?.cause,
      reported: options?.reported,
    });
    this.name = 'ExternalServiceError';
  }
}

export class DatabaseError extends AppError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, {
      code: 'DATABASE_ERROR',
      statusCode: 500,
      cause: options?.cause,
    });
    this.name = 'DatabaseError';
  }
}

/** Stable machine code for an org IP-allowlist block — clients switch on it to
 *  redirect to the "IP not allowed" page rather than showing a generic 403. */
export const IP_NOT_ALLOWED_CODE = 'IP_NOT_ALLOWED';

/**
 * Thrown when a request's client IP is not on the active organization's IP
 * allowlist. A 403 with its own stable `code` so the client can distinguish an
 * IP block (redirect to the blocked page) from an ordinary permission failure.
 * Enforced at the org-scoping choke point (`requireActiveOrg`), so it guards
 * both page loads and every org-scoped API call.
 */
export class IpNotAllowedError extends AppError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, {
      code: IP_NOT_ALLOWED_CODE,
      statusCode: 403,
      cause: options?.cause,
    });
    this.name = 'IpNotAllowedError';
  }
}

/**
 * Thrown when a *programmer* error is detected at runtime — typically a
 * missing/empty env var that should have been validated at bootstrap, or
 * an unexpected response shape from a trusted external service. Distinct
 * from `ValidationError` (which is about untrusted user input) and from
 * `ExternalServiceError` (which is about an external service failing).
 *
 * Surface as 500 — these aren't user-correctable. The `code` lets
 * dashboards filter for "the runtime is broken" separately from generic
 * failures.
 */
export class ConfigurationError extends AppError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, {
      code: 'CONFIGURATION_ERROR',
      statusCode: 500,
      cause: options?.cause,
    });
    this.name = 'ConfigurationError';
  }
}
